import React from 'react';
import { Code } from 'lucide-react';
import Sidebar from '../components/Sidebar';

const APIDocs = () => {
  const endpoints = [
    {
      category: 'Authentication',
      routes: [
        { method: 'POST', path: '/api/auth/register', description: 'Register new user and create tenant' },
        { method: 'POST', path: '/api/auth/login', description: 'User login' },
        { method: 'GET', path: '/api/auth/me', description: 'Get current user info' },
      ],
    },
    {
      category: 'Pages',
      routes: [
        { method: 'GET', path: '/api/pages', description: 'Get all pages for tenant' },
        { method: 'POST', path: '/api/pages', description: 'Create new page' },
        { method: 'GET', path: '/api/pages/{page_id}', description: 'Get page by ID' },
        { method: 'PUT', path: '/api/pages/{page_id}', description: 'Update page' },
        { method: 'DELETE', path: '/api/pages/{page_id}', description: 'Delete page' },
      ],
    },
    {
      category: 'Newsletters',
      routes: [
        { method: 'GET', path: '/api/newsletters', description: 'Get all newsletters' },
        { method: 'POST', path: '/api/newsletters', description: 'Create newsletter' },
        { method: 'GET', path: '/api/newsletters/{id}', description: 'Get newsletter by ID' },
        { method: 'PUT', path: '/api/newsletters/{id}', description: 'Update newsletter' },
        { method: 'POST', path: '/api/newsletters/{id}/send', description: 'Send newsletter to recipients' },
      ],
    },
    {
      category: 'Media',
      routes: [
        { method: 'GET', path: '/api/media', description: 'Get all media files' },
        { method: 'POST', path: '/api/media/upload', description: 'Upload media file' },
        { method: 'DELETE', path: '/api/media/{media_id}', description: 'Delete media file' },
      ],
    },
  ];

  return (
    <div className="flex" data-testid="api-docs-page">
      <Sidebar />
      
      <div className="flex-1 min-w-0 bg-slate-50 p-8">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              API Documentation
            </h1>
            <p className="text-lg text-slate-600">
              Use the NexusCMS REST API to build custom integrations and headless applications.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">Authentication</h2>
            <p className="text-slate-600 mb-4">
              All API requests require authentication using a Bearer token. Include the token in the Authorization header:
            </p>
            <div className="bg-slate-900 text-slate-100 p-4 rounded-lg font-mono text-sm">
              Authorization: Bearer YOUR_ACCESS_TOKEN
            </div>
          </div>

          {endpoints.map((section) => (
            <div key={section.category} className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
              <h2 className="text-2xl font-semibold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {section.category}
              </h2>
              <div className="space-y-4">
                {section.routes.map((route, idx) => (
                  <div key={idx} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <span
                        className={`px-3 py-1 rounded text-xs font-semibold ${
                          route.method === 'GET'
                            ? 'bg-blue-100 text-blue-700'
                            : route.method === 'POST'
                            ? 'bg-emerald-100 text-emerald-700'
                            : route.method === 'PUT'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {route.method}
                      </span>
                      <code className="text-sm font-mono">{route.path}</code>
                    </div>
                    <p className="text-slate-600 text-sm">{route.description}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6">
            <div className="flex gap-4">
              <Code size={24} className="text-indigo-600 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-indigo-900 mb-2">Need More Details?</h3>
                <p className="text-indigo-700 text-sm">
                  For detailed request/response schemas, code examples, and interactive API testing,
                  check out our full API documentation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APIDocs;
